// src/stores/auth.ts
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

import { type JwtPayload, jwtDecode } from 'jwt-decode'

import { apiFetch, setUnauthorizedHandler } from '@/api'
import { getInitData, isInsideTelegram } from '@/telegramWebApp'

export type User = {
  id: number
  telegram_id: number
  fullname?: string | null
  countdown_seconds: number
}

export type TelegramAuth = {
  bot: string
  auth_date: number
  id: number
  first_name: string
  last_name: string
  photo_url: string
  username: string
}

export type JwtTgPayload = JwtPayload & {
  sub: string
  tg_id: number
  tg: TelegramAuth
  exp: number
}

// The access token is renewed this long before it runs out, so an open Mini App never sits on an expired one.
export const REFRESH_MARGIN_MS = 5 * 60 * 1000
// setTimeout overflows after about 24.8 days, and a long wait is checked again anyway (sleeping devices).
const MAX_TIMER_MS = 12 * 60 * 60 * 1000

type AuthResponse = { token?: string; refresh_token?: string | null }

export const useAuthStore = defineStore('auth', () => {
  // ── State ─────────────────────────────────────────────────────
  const token = ref<string | null>(localStorage.getItem('auth_token'))
  const refreshToken = ref<string | null>(localStorage.getItem('auth_refresh_token'))
  const user = ref<User | null>(null)
  const loading = ref(false)

  // ── Actions ───────────────────────────────────────────────────
  const setToken = (newToken: string | null) => {
    token.value = newToken
    if (newToken) {
      localStorage.setItem('auth_token', newToken)
    } else {
      localStorage.removeItem('auth_token')
    }
  }

  const setRefreshToken = (newToken: string | null) => {
    refreshToken.value = newToken
    if (newToken) {
      localStorage.setItem('auth_refresh_token', newToken)
    } else {
      localStorage.removeItem('auth_refresh_token')
    }
  }

  // Keeps the answer of any login route: the access token and the refresh token that renews it.
  const setSession = (newToken: string, newRefreshToken?: string | null) => {
    setToken(newToken)
    setRefreshToken(newRefreshToken ?? null)
  }

  const loadMe = async () => {
    if (!token.value) {
      user.value = null
      return
    }
    loading.value = true
    try {
      const resp = await apiFetch<User>('/auth/me')
      user.value = resp.ok ? await resp.json() : null
    } finally {
      loading.value = false
    }
  }

  const logout = () => {
    const oldRefreshToken = refreshToken.value
    if (oldRefreshToken) {
      // Revokes it on the server, nothing to wait for or to do if that fails.
      void apiFetch('/auth/logout', {
        method: 'POST',
        json: { refresh_token: oldRefreshToken },
      }).catch(() => undefined)
    }
    setToken(null)
    setRefreshToken(null)
    user.value = null
  }

  const payload = computed<JwtTgPayload | null>(() => {
    if (!token.value) return null
    try {
      return jwtDecode(token.value) as JwtTgPayload
    } catch {
      return null // (a malformed stored token must not break the watchers, the first 401 logs it out)
    }
  })

  // True while the stored token exists and has not expired yet.
  const hasValidToken = computed(() => {
    const exp = payload.value?.exp
    return !!token.value && (!exp || exp * 1000 > Date.now())
  })

  // Trades the signed `initData` for a session, without loading the user (`recover()` must not wait for a request that
  // would wait for the recovery itself).
  const exchangeInitData = async () => {
    const initData = getInitData()
    if (!initData) throw new Error('Not opened inside Telegram.')
    const resp = await apiFetch('/auth/telegram-webapp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ init_data: initData }),
    })
    if (!resp.ok) {
      const body = (await resp.json().catch(() => ({}))) as { detail?: string }
      throw new Error(`Login failed (${resp.status})${body.detail ? `: ${body.detail}` : ''}`)
    }
    const data = (await resp.json()) as AuthResponse
    if (!data.token) throw new Error('No token received.')
    setSession(data.token, data.refresh_token)
  }

  // Login inside the Telegram Mini App: the signed `initData` replaces the Login Widget. Throws on failure.
  const loginWithWebApp = async () => {
    await exchangeInitData()
    await loadMe()
  }

  // Login in a plain browser with "Log in with Telegram": trades the popup's OpenID Connect id_token (and the nonce the
  // backend handed out with `/auth/config`) for the app's token. Throws on failure.
  const loginWithTelegramLogin = async (idToken: string, nonce: string) => {
    const resp = await apiFetch('/auth/telegram-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: idToken, nonce }),
    })
    if (!resp.ok) {
      const body = (await resp.json().catch(() => ({}))) as { detail?: string }
      throw new Error(`Login failed (${resp.status})${body.detail ? `: ${body.detail}` : ''}`)
    }
    const data = (await resp.json()) as AuthResponse
    if (!data.token) throw new Error('No token received.')
    setSession(data.token, data.refresh_token)
    await loadMe()
  }

  // Trades the refresh token for a new pair. After a lost race with another tab (which already rotated it and stored the
  // new one) the stored token differs from the one sent, so that one is tried once more.
  const refreshSession = async (): Promise<boolean> => {
    for (let attempt = 0; attempt < 2; attempt++) {
      const sent = localStorage.getItem('auth_refresh_token')
      if (!sent) return false
      const resp = await apiFetch<AuthResponse>('/auth/refresh', {
        method: 'POST',
        json: { refresh_token: sent },
      })
      if (resp.ok) {
        const data = await resp.json()
        if (!data.token) return false
        setSession(data.token, data.refresh_token)
        return true
      }
      if (resp.status !== 401 || localStorage.getItem('auth_refresh_token') === sent) return false
    }
    return false
  }

  // One recovery at a time, however many requests hit the expired token at once.
  let recovering: Promise<boolean> | null = null

  // Gets a working token again: the refresh token first, inside Telegram the fresh initData as the fallback (the
  // webview can have been open longer than the refresh token lives), otherwise the user is logged out.
  const recover = (): Promise<boolean> => {
    recovering ??= (async () => {
      try {
        if (await refreshSession()) return true
        if (isInsideTelegram()) {
          await exchangeInitData()
          return true
        }
      } catch (e) {
        console.warn('Recovering the sign-in failed', e)
      }
      logout()
      return false
    })().finally(() => {
      recovering = null
    })
    return recovering
  }

  // Renews the token if it is about to run out, but only where that can work.
  const ensureFresh = async (): Promise<void> => {
    const exp = payload.value?.exp
    if (!token.value || !exp || exp * 1000 - Date.now() > REFRESH_MARGIN_MS) return
    if (!refreshToken.value && !isInsideTelegram()) return
    await recover()
  }

  let timer: ReturnType<typeof setTimeout> | null = null
  const scheduleRefresh = () => {
    if (timer) clearTimeout(timer)
    timer = null
    const exp = payload.value?.exp
    if (!token.value || !exp) return
    const delay = Math.min(Math.max(exp * 1000 - REFRESH_MARGIN_MS - Date.now(), 0), MAX_TIMER_MS)
    timer = setTimeout(() => {
      void ensureFresh().then(scheduleRefresh)
    }, delay)
  }
  watch(token, scheduleRefresh, { immediate: true })

  // A Mini App that was in the background wakes up with a stale token, and the timers may not have run meanwhile.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void ensureFresh()
  })

  setUnauthorizedHandler(recover)

  // Whenever the token changes, re‑fetch the current user.
  watch(
    token,
    async () => {
      await loadMe()
    },
    { immediate: true },
  )

  // Expose everything the store should provide
  return {
    token,
    refreshToken,
    user,
    loading,
    setToken,
    setSession,
    loadMe,
    logout,
    payload,
    hasValidToken,
    loginWithWebApp,
    loginWithTelegramLogin,
    recover,
    ensureFresh,
  }
})
